import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import CommentIcon from '@mui/icons-material/Comment';

import TextHighlight from './TextHighlight';
import Navigation from './Navigation';

interface ZendeskComment {
  id: number;
  ticket_id: number;
  author_name: string | null;
  body: string;
  html_body: string;
  plain_body: string;
  public: boolean;
  created_at: string;
  classification: string | null;
  secondary_classification: string | null;
  redact: boolean;
  is_matching: boolean;
}

interface ZendeskTicket {
  id: number;
  external_id: string | null;
  subject: string;
  description: string;
  status: string;
  priority: string | null;
  type: string;
  created_at: string;
  updated_at: string;
  submitter_name: string | null;
  requester_name: string | null;
  assignee_name: string | null;
  classification: string | null;
  secondary_classification: string | null;
  redact: boolean;
  tags: string[];
  comments: ZendeskComment[];
  is_matching: boolean;
}

interface Filters {
  keyword: string;
  status: string;
  priority: string;
  requester: string;
  assignee: string;
  startDate: Date | null;
  endDate: Date | null;
  classification: string;
  redact: string;
}

const ZendeskView: React.FC = () => {
  const [tickets, setTickets] = useState<ZendeskTicket[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [priorities, setPriorities] = useState<string[]>([]);
  // Do not fetch giant user lists; allow free-text filters instead per product decision
  const [filters, setFilters] = useState<Filters>({
    keyword: '',
    status: '',
    priority: '',
    requester: '',
    assignee: '',
    startDate: null,
    endDate: null,
    classification: '',
    redact: '',
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const hasKeyword = filters.keyword.trim().length > 0;

  /**
   * Fetch distinct Zendesk ticket statuses from the backend.
   *
   * Purpose: Populate a small, stable dropdown to keep the UI responsive
   * without loading heavy user datasets, aligning with the new product rules.
   */
  /**
   * fetchStatuses: Retrieve distinct Zendesk ticket statuses.
   *
   * Purpose: Populate a small, stable dropdown to keep the UI responsive.
   */
  const fetchStatuses = useCallback(async () => {
    try {
      const response = await fetch('/api/zendesk/statuses', { credentials: 'include' });
      const data = await response.json();
      setStatuses(data.statuses || []);
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  }, []);

  /**
   * Fetch distinct Zendesk ticket priorities from the backend.
   *
   * Purpose: Provide lightweight filter values that do not require large
   * preloads, avoiding the prior crash with user lists.
   */
  /**
   * fetchPriorities: Retrieve distinct Zendesk ticket priorities.
   *
   * Purpose: Provide lightweight filter values that do not require large
   * preloads, avoiding the prior crash with user lists.
   */
  const fetchPriorities = useCallback(async () => {
    try {
      const response = await fetch('/api/zendesk/priorities', { credentials: 'include' });
      const data = await response.json();
      setPriorities(data.priorities || []);
    } catch (error) {
      console.error('Error fetching priorities:', error);
    }
  }, []);

  /**
   * Load tickets for the current page and filters, embedding comments.
   *
   * Reason: Implements the ticket + comments display. A keyword match in any
   * comment returns the ticket. No channel filter is used by design.
   */
  /**
   * fetchTickets: Load tickets with embedded comments for current filters/page.
   *
   * Reason: Implements the ticket + comments display. Memoized on filter
   * inputs so effects can safely depend on it.
   */
  const fetchTickets = useCallback(async (currentPage: number) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: String(currentPage),
        per_page: '10',
      });
      if (filters.keyword) queryParams.set('keyword', filters.keyword);
      if (filters.status) queryParams.set('status', filters.status);
      if (filters.priority) queryParams.set('priority', filters.priority);
      if (filters.requester) queryParams.set('requester', filters.requester);
      if (filters.assignee) queryParams.set('assignee', filters.assignee);
      if (filters.startDate) queryParams.set('start_date', filters.startDate.toISOString());
      if (filters.endDate) queryParams.set('end_date', filters.endDate.toISOString());
      if (filters.classification) queryParams.set('classification', filters.classification);
      if (filters.redact) queryParams.set('redact', filters.redact);

      const response = await fetch(`/api/zendesk/tickets?${queryParams.toString()}`, { credentials: 'include' });
      const data = await response.json();
      setTickets(data.tickets || []);
      setTotalPages(data.pages || 0);
      setTotalTickets(data.total || 0);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [filters.keyword, filters.status, filters.priority, filters.requester, filters.assignee, filters.startDate, filters.endDate, filters.classification, filters.redact]);

  // Initialize static dropdown data
  useEffect(() => {
    fetchStatuses();
    fetchPriorities();
  }, [fetchStatuses, fetchPriorities]);

  // Re-fetch tickets when page changes and a keyword search is active
  useEffect(() => {
    if (hasKeyword) {
      fetchTickets(page);
    }
  }, [page, hasKeyword, fetchTickets]);

  /**
   * Compute MUI chip color for ticket status.
   *
   * Purpose: Provide quick visual scanning of ticket lifecycle state.
   */
  const getStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'open':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'solved':
      case 'closed':
        return 'success';
      default:
        return 'default';
    }
  };

  /**
   * Compute MUI chip color for ticket priority.
   *
   * Reason: Surface urgency at-a-glance without reading details.
   */
  const getPriorityColor = (priority: string | null) => {
    switch ((priority || '').toLowerCase()) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  /**
   * Compute MUI chip color for legal classification tags if present.
   *
   * Purpose: Read-only visualization only; editing is disabled per new scope.
   */
  const getClassificationColor = (classification: string | null) => {
    switch (classification) {
      case 'produce':
        return 'success';
      case 'do_not_produce':
        return 'error';
      case 'attorney_client_privilege':
        return 'warning';
      default:
        return 'default';
    }
  };

  /**
   * Download the current ticket search result as a JSON file.
   *
   * Reason: Mirrors Slack export behavior while using ticket-centric data.
   */
  const handleDownload = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.keyword) queryParams.set('keyword', filters.keyword);
      if (filters.status) queryParams.set('status', filters.status);
      if (filters.priority) queryParams.set('priority', filters.priority);
      if (filters.requester) queryParams.set('requester', filters.requester);
      if (filters.assignee) queryParams.set('assignee', filters.assignee);
      if (filters.startDate) queryParams.set('start_date', filters.startDate.toISOString());
      if (filters.endDate) queryParams.set('end_date', filters.endDate.toISOString());
      if (filters.classification) queryParams.set('classification', filters.classification);
      if (filters.redact) queryParams.set('redact', filters.redact);

      const response = await fetch(`/api/zendesk/tickets/download?${queryParams.toString()}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to download');
      const data = await response.json();

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().split('T')[0];
      a.download = `zendesk-tickets-${date}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading tickets:', error);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Navigation />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <Typography variant="h4" color="primary">Zendesk Ticket Viewer</Typography>
            <Typography variant="body2" color="text.secondary">Search and review support tickets with comments</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="outlined" startIcon={<FilterListIcon />} onClick={() => setOpenFiltersModal(true)} fullWidth sx={{ height: '56px' }}>Search & Filters</Button>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button variant="contained" color="primary" onClick={handleDownload} startIcon={<DownloadIcon />} fullWidth sx={{ height: '56px' }} disabled={!filters.keyword}>Export Results</Button>
          </Grid>
        </Grid>
      </Paper>

      <Dialog open={openFiltersModal} onClose={() => setOpenFiltersModal(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Search & Filters</Typography>
          <IconButton onClick={() => setOpenFiltersModal(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Keywords (one per line)"
                value={filters.keyword}
                onChange={(e) => setFilters({ ...filters, keyword: e.target.value })}
                multiline
                rows={4}
                required
                helperText="Enter keywords, one per line. Tickets with matching comments or fields will be included."
                placeholder={`Enter keywords...\ne.g., account issue\nbilling problem\npassword reset`}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Additional Filters</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {statuses.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={filters.priority} label="Priority" onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
                  <MenuItem value="">All Priorities</MenuItem>
                  {priorities.map((p) => (<MenuItem key={p} value={p}>{p}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Requester (name contains)" value={filters.requester} onChange={(e) => setFilters({ ...filters, requester: e.target.value })} placeholder="Optional requester name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Assignee (name contains)" value={filters.assignee} onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} placeholder="Optional assignee name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Classification</InputLabel>
                <Select value={filters.classification} label="Classification" onChange={(e) => setFilters({ ...filters, classification: e.target.value })}>
                  <MenuItem value="">All Classifications</MenuItem>
                  <MenuItem value="produce">Produce</MenuItem>
                  <MenuItem value="do_not_produce">Do Not Produce</MenuItem>
                  <MenuItem value="attorney_client_privilege">Attorney-Client Privilege</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Redaction Status</InputLabel>
                <Select value={filters.redact} label="Redaction Status" onChange={(e) => setFilters({ ...filters, redact: e.target.value })}>
                  <MenuItem value="">All Items</MenuItem>
                  <MenuItem value="true">Redacted Only</MenuItem>
                  <MenuItem value="false">Non-Redacted Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Date Range</Typography>
            </Grid>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid item xs={12} sm={6}>
                <DatePicker label="Start Date" value={filters.startDate} onChange={(d) => setFilters({ ...filters, startDate: d })} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker label="End Date" value={filters.endDate} onChange={(d) => setFilters({ ...filters, endDate: d })} slotProps={{ textField: { fullWidth: true } }} />
              </Grid>
            </LocalizationProvider>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFilters({ keyword: '', status: '', priority: '', requester: '', assignee: '', startDate: null, endDate: null, classification: '', redact: '' })} color="secondary">Clear All</Button>
          <Button onClick={() => { setOpenFiltersModal(false); setPage(1); fetchTickets(1); }} variant="contained" disabled={!filters.keyword.trim() || loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}>
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </DialogActions>
      </Dialog>

      {!filters.keyword && (
        <Alert severity="info" sx={{ mb: 2 }}>Click "Search & Filters" to enter keywords and search tickets with comments.</Alert>
      )}

      {loading && (
        <Paper sx={{ p: 4, mb: 2, textAlign: 'center' }}>
          <CircularProgress size={40} />
          <Typography variant="h6" sx={{ mt: 2 }}>Searching tickets...</Typography>
          <Typography variant="body2" color="text.secondary">This may take a moment for large result sets</Typography>
        </Paper>
      )}

      {!loading && tickets.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>Found {totalTickets} tickets matching your search criteria</Alert>
      )}

      {!loading && tickets.map((ticket) => (
        <Paper key={ticket.id} sx={{ mb: 3, backgroundColor: '#f9f9f9' }}>
          <Card>
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ConfirmationNumberIcon color="primary" />
                      <Typography variant="h6" color="primary">
                        Ticket #{ticket.id}
                        {ticket.is_matching && (<Chip label="MATCH" size="small" color="primary" sx={{ ml: 1 }} />)}
                      </Typography>
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      <TextHighlight text={ticket.subject} highlight={filters.keyword} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {ticket.status && (<Chip label={ticket.status} color={getStatusColor(ticket.status)} size="small" />)}
                      {ticket.priority && (<Chip label={ticket.priority} color={getPriorityColor(ticket.priority)} size="small" />)}
                      {/**
                       * Hide the 'produce' classification tag per product decision that it's irrelevant for review.
                       * We keep other classification tags for context, but omit 'produce' specifically.
                       */}
                      {ticket.classification && ticket.classification !== 'produce' && (
                        <Chip label={ticket.classification.replace('_', ' ')} color={getClassificationColor(ticket.classification)} size="small" />
                      )}
                      {ticket.redact && (<Chip label="REDACTED" color="error" size="small" />)}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={3}><Typography variant="body2" color="text.secondary">Created: {new Date(ticket.created_at).toLocaleDateString()}</Typography></Grid>
                <Grid item xs={12} sm={6} md={3}><Typography variant="body2" color="text.secondary">Requester: {ticket.requester_name || 'Unknown'}</Typography></Grid>
                <Grid item xs={12} sm={6} md={3}><Typography variant="body2" color="text.secondary">Assignee: {ticket.assignee_name || 'Unassigned'}</Typography></Grid>
                <Grid item xs={12} sm={6} md={3}><Typography variant="body2" color="text.secondary">Comments: {ticket.comments.length}</Typography></Grid>
              </Grid>

              {ticket.description && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <TextHighlight text={ticket.description} highlight={filters.keyword} />
                  </Typography>
                </Box>
              )}

              {ticket.tags && ticket.tags.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Tags:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {ticket.tags.map((tag, idx) => (<Chip key={idx} label={tag} size="small" variant="outlined" />))}
                  </Box>
                </Box>
              )}

              {ticket.comments.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`comments-${ticket.id}`} id={`comments-header-${ticket.id}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CommentIcon />
                      <Typography variant="h6">Comments ({ticket.comments.length})</Typography>
                      {ticket.comments.some((c) => c.is_matching) && (<Chip label="HAS MATCHES" size="small" color="primary" />)}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {ticket.comments.map((comment) => (
                      <Paper key={comment.id} sx={{ p: 2, mb: 2, backgroundColor: 'white', border: comment.is_matching ? '2px solid' : '1px solid #e0e0e0', borderColor: comment.is_matching ? 'primary.main' : '#e0e0e0', boxShadow: comment.is_matching ? '0 0 8px rgba(46, 125, 50, 0.3)' : 'none' }}>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            <strong>{comment.author_name || 'Unknown Author'}</strong> • {new Date(comment.created_at).toLocaleString()} • {comment.public ? 'Public' : 'Private'}
                            {comment.is_matching && (<Typography component="span" variant="body2" color="primary" sx={{ ml: 1, fontWeight: 'bold' }}>• MATCH</Typography>)}
                            {/**
                             * Hide 'produce' tag on individual comments as well, to match ticket-level behavior.
                             */}
                            {comment.classification && comment.classification !== 'produce' && (
                              <Chip label={comment.classification.replace('_', ' ')} color={getClassificationColor(comment.classification)} size="small" sx={{ ml: 1 }} />
                            )}
                            {comment.redact && (<Chip label="REDACTED" color="error" size="small" sx={{ ml: 1 }} />)}
                          </Typography>
                        </Box>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          <TextHighlight text={comment.body} highlight={filters.keyword} />
                        </Typography>
                      </Paper>
                    ))}
                  </AccordionDetails>
                </Accordion>
              )}
            </CardContent>
          </Card>
        </Paper>
      ))}

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" />
        </Box>
      )}
    </Box>
  );
};

export default ZendeskView;


